import {ProposalFilter} from "../../service/proposal.service";
import {ModalWindow} from "../modal/ModalWindow";
import {useState} from "react";
import {ProposalFilterComponent} from "./ProposalFilterComponent";
import {Box, Button, Skeleton, Stack, Typography} from "@mui/material";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {ProposalSubscriptionComponent} from "./ProposalSubscriptionComponent";
import {DefaultError} from "@tanstack/query-core";
import {enqueueSnackbar} from "notistack";
import {createSubscription, loadSubscriptions, updateSubscription} from "../../service/subscription.service";
import {Subscription} from "../../types/Subscription";
import {usePropFilterStore} from "../../store/prop.filter.store";

interface SubscriptionModalProps {
    onClose: () => void
}


export function SubscriptionModal({onClose}:SubscriptionModalProps) {
    const [selectedSubscription, setSelectedSubscription] = useState<Subscription>();
    const [subscriptionFilter, _setSubscriptionFilter] = useState<ProposalFilter>();
    const {filter} = usePropFilterStore();
    function setSubscriptionFilter(newFilter: ProposalFilter) {
        _setSubscriptionFilter(newFilter);
        setSelectedSubscription({...selectedSubscription!, content: JSON.stringify(newFilter)});
        setMode("filter")
    }
    const [mode, setMode] = useState<"overview" | "filter" | "created">("overview");
    const queryClient = useQueryClient();

    const { data, isFetching, error } = useQuery({
        queryKey: ['subscriptions', "my"],
        refetchOnWindowFocus: false,
        queryFn: () => {
            return loadSubscriptions()
                .then(response => {
                    return response.data
                })
        }
    });

    const updateMutation = useMutation<void, DefaultError, void>({
        mutationFn: () => (selectedSubscription!.id
            ? updateSubscription(selectedSubscription!.id, selectedSubscription!)
            : createSubscription(selectedSubscription!))
                .then(() => void 0),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['subscriptions', "my"] }).catch(e => {
                enqueueSnackbar({ message: "Failed to invalidate subscriptions query: " + e.message, variant: 'error' })
            });
            if(!selectedSubscription!.id) {
                setMode("created")
            } else {
                enqueueSnackbar({message: "Subscription Updated", variant: "success"})
                onClose()
            }
        },
        onError: (error) => {
            enqueueSnackbar({ message: "Failed to persist subscription: " + error.message, variant: 'error' })
        }
    });

    return <ModalWindow width={460} loading={isFetching} title={"Subscriptions"} onClose={onClose}>
        {isFetching && <Stack>
            <Skeleton width={'90%'}/>
            <Skeleton width={'70%'}/>
            <Skeleton width={'40%'}/>
            <Skeleton width={'80%'}/>
            <Skeleton width={'50%'}/>
        </Stack>}
        {mode === "overview" && <ProposalSubscriptionComponent
            onClose={onClose}
            subscriptions={data || []}
            setSelectedSubscription={(subscription) => {
                try {
                    setSubscriptionFilter(JSON.parse(subscription.content || "{}"));
                } catch (e) {
                    setSubscriptionFilter({
                        page: 0,
                        size: 20,
                        sortKey: "id",
                        sortOrder: "asc",
                        proposalStatuses: []
                    })
                }
                setSelectedSubscription(subscription);
            }}
            error={error}
        />}
        {mode === 'filter' && <Box>
            <Typography>
                Review the available filters and adjust them as you see fit.
                Once a day, we will apply these filters to all new real estate listings and send you a report by email.
            </Typography>
            <ProposalFilterComponent
            small
            hideMy
            hideFavorite
            hideAddNew
            filter={subscriptionFilter!}
            setFilter={setSubscriptionFilter} />
        </Box>}
        {mode === "created" && <Stack>
            <Typography>Your subscription has been created successfully.</Typography>
            <Typography>Once a day, you will receive a feed of listings that match your search criteria.</Typography>
        </Stack>}
        <Stack flexDirection={"row"} justifyContent={"space-between"}>
            <Button onClick={() => onClose()}>Close</Button>
            {mode === "overview" && !isFetching && data && data.length === 0 && <Button onClick={() => {
                setSelectedSubscription({type: 'AD_SUBSCRIPTION', content: "{}"} as Subscription);
                setSubscriptionFilter(filter);
            }}>Create New Subscription</Button>}
            {mode === 'filter' && <Button onClick={() => updateMutation.mutate()}>Save</Button>}
        </Stack>
    </ModalWindow>
}
